import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Paper
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { colors } from '../../utils/colors';
import BootstrapFormField from '../../components/common/FormField';
import BootstrapSelectField from '../../components/common/SelectField';
import BootstrapDateField from '../../components/common/DateField';
import FileUpload from '../../components/common/FileUpload';

const AddProcurement = ({ customerId, projectId, onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    itemDescription: '',
    category: '',
    subCategory: '',
    quantity: '',
    unitPrice: '',
    totalAmount: '',
    supplier: '',
    requiredDate: '',
    justification: '',
    file: null
  });

  const [errors, setErrors] = useState({});

  const categoryOptions = [
    'Electronics',
    'Furniture',
    'Office Supplies',
    'Equipment',
    'Services',
    'Materials',
    'Software',
    'Other'
  ];

  const subCategoryOptions = {
    Electronics: ['Computers', 'Phones', 'Accessories', 'Networking'],
    Furniture: ['Desks', 'Chairs', 'Storage', 'Meeting Tables'],
    'Office Supplies': ['Stationery', 'Paper', 'Writing Tools', 'Organizers'],
    Equipment: ['Machinery', 'Tools', 'Safety Equipment', 'Testing Equipment'],
    Services: ['Consulting', 'Maintenance', 'Training', 'Support'],
    Materials: ['Raw Materials', 'Construction', 'Consumables', 'Packaging'],
    Software: ['Licenses', 'Subscriptions', 'Development Tools', 'Cloud Services'],
    Other: ['Miscellaneous']
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (field === 'quantity' || field === 'unitPrice') {
      const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(formData.quantity) || 0;
      const price = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(formData.unitPrice) || 0;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        totalAmount: (qty * price).toFixed(2)
      }));
    }

    if (field === 'category') {
      setFormData((prev) => ({
        ...prev,
        category: value,
        subCategory: ''
      }));
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      file: file
    }));
    if (errors.file) {
      setErrors((prev) => ({
        ...prev,
        file: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.itemDescription.trim()) {
      newErrors.itemDescription = 'Item description is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.subCategory) {
      newErrors.subCategory = 'Sub category is required';
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = 'Valid quantity is required';
    }
    if (!formData.unitPrice || parseFloat(formData.unitPrice) <= 0) {
      newErrors.unitPrice = 'Valid unit price is required';
    }
    if (!formData.supplier.trim()) {
      newErrors.supplier = 'Supplier is required';
    }
    if (!formData.requiredDate) {
      newErrors.requiredDate = 'Required date is required';
    }
    if (!formData.file) {
      newErrors.file = 'Please attach a document';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
      setFormData({
        itemDescription: '',
        category: '',
        subCategory: '',
        quantity: '',
        unitPrice: '',
        totalAmount: '',
        supplier: '',
        requiredDate: '',
        justification: '',
        file: null
      });
      setErrors({});
      onBack();
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#ffffff', minHeight: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{
            color: colors.primary.main,
            bgcolor: colors.background.hover,
            '&:hover': {
              bgcolor: colors.primary.light + '20'
            },
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 }
          }}
        >
          <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: colors.text.primary,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
          }}
        >
          Purchase Request
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          border: `1px solid ${colors.border.main}`,
          bgcolor: colors.background.primary
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Grid item xs={12}>
            <BootstrapFormField
              label="Item description*"
              value={formData.itemDescription}
              onChange={handleChange('itemDescription')}
              placeholder="Write description"
              multiline
              rows={2}
              error={!!errors.itemDescription}
              helperText={errors.itemDescription}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <BootstrapSelectField
              label="Category*"
              value={formData.category}
              onChange={handleChange('category')}
              options={categoryOptions}
              placeholder="Select category"
              error={!!errors.category}
              helperText={errors.category}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <BootstrapSelectField
              label="Sub category*"
              value={formData.subCategory}
              onChange={handleChange('subCategory')}
              options={formData.category ? subCategoryOptions[formData.category] || [] : []}
              placeholder="Add sub category"
              disabled={!formData.category}
              error={!!errors.subCategory}
              helperText={errors.subCategory}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <BootstrapFormField
              label="Quantity*"
              value={formData.quantity}
              onChange={handleChange('quantity')}
              type="number"
              placeholder="Add quantity"
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <BootstrapFormField
              label="Unit price*"
              value={formData.unitPrice}
              onChange={handleChange('unitPrice')}
              type="number"
              placeholder="Add price"
              error={!!errors.unitPrice}
              helperText={errors.unitPrice}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <BootstrapFormField
              label="Total Amount*"
              value={formData.totalAmount}
              onChange={handleChange('totalAmount')}
              type="number"
              placeholder="Add total amount"
              disabled
              error={!!errors.totalAmount}
              helperText={errors.totalAmount}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <BootstrapFormField
              label="Supplier*"
              value={formData.supplier}
              onChange={handleChange('supplier')}
              placeholder="Add supplier"
              error={!!errors.supplier}
              helperText={errors.supplier}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <BootstrapDateField
              label="Required date*"
              value={formData.requiredDate}
              onChange={handleChange('requiredDate')}
              placeholder="dd/mm/yyyy"
              error={!!errors.requiredDate}
              helperText={errors.requiredDate}
            />
          </Grid>

          <Grid item xs={12}>
            <BootstrapFormField
              label="Justification"
              value={formData.justification}
              onChange={handleChange('justification')}
              placeholder="Write here"
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <FileUpload
              label="Attach file*"
              onChange={handleFileChange}
              error={!!errors.file}
              helperText={errors.file}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"/>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end',
            gap: 2,
            mt: { xs: 3, sm: 4 },
            pt: { xs: 2, sm: 3 },
            borderTop: `1px solid ${colors.border.main}`
          }}
        >
          <Button
            onClick={onBack}
            variant="outlined"
            sx={{
              borderColor: colors.border.main,
              color: colors.text.secondary,
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: 1.2,
              borderRadius: 2,
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: colors.text.secondary,
                bgcolor: colors.background.hover
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: colors.primary.main,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 3, sm: 4 },
              py: 1.2,
              borderRadius: 2,
              width: { xs: '100%', sm: 'auto' },
              boxShadow: `0 4px 12px ${colors.primary.main}40`,
              '&:hover': {
                bgcolor: colors.primary.dark,
                boxShadow: `0 6px 16px ${colors.primary.main}50`
              }
            }}
          >
            Submit Request
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddProcurement;
